import { Popover } from '@nextui-org/react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { FadeInAnimation } from '../../components';
import { useFilterContext } from '../../contexts/FiltersContext';
import { inMobile, useWindowDimensions } from '../../hooks/useDimensions';
import Filter from '../../models/Filter';
import {
  FilterActionButtonsContainer,
  FilterComponentContainer,
  FilterContent,
  FilterContentContainer,
  StyledPopoverContent,
} from './BaseFilterComponent.styles';
import {
  FilterItem,
  FilterItemContainer,
  FilterItemIcon,
  FilterItemInformation,
  FilterItemSelectedValue,
  FilterItemTitle,
  StyledButton,
  StyledSecondaryButton,
  StyledShimmerTitle,
} from './FiltersList.styles';

type BaseFilterComponentInput = {
  title: string;
  icon: React.ReactElement;
  getFilter?: () => Filter | undefined;
  children: any;
  onOpen?: any;
  onCancel: () => void;
  onClearAll?: () => void;
  onApply?: () => void;
  displayOptions?: boolean;
  triggerClose?: boolean;
  inUse?: boolean;
  storedFilter?: Filter;
  printedValues?: string;
  noPadding?: boolean;
  disableFilterButton?: boolean;
  loading?: boolean;
};

const BaseFilterComponent = ({
  title,
  icon,
  getFilter,
  children,
  onOpen,
  onCancel,
  onClearAll,
  onApply,
  triggerClose,
  inUse,
  storedFilter,
  printedValues,
  disableFilterButton,
  loading,
}: BaseFilterComponentInput) => {
  // Dependencies
  const { t } = useTranslation();
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = inMobile(windowWidth);
  const { handleApplyFilter, setOpenFiltersMobile, setSelectedFilterTitle, selectedFilterTitle } = useFilterContext();

  // Effects
  React.useEffect(() => {
    if (triggerClose) {
      setSelectedFilterTitle('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerClose]);

  // Handlers
  const applyOnClick = () => {
    if (!getFilter) return;
    const filter = getFilter();
    handleApplyFilter(filter);
    onApply?.();
    /* This setTimeOut is because when setOpenFiltersMobile(false) all the content is unMounted,
       And some effects in useSelectionFilter are not being executed. */
    setTimeout(() => {
      setOpenFiltersMobile(false);
      setSelectedFilterTitle('');
    }, 100);
  };

  const secondaryButtonOnClick = () => {
    if (!inMobile(windowWidth)) {
      onCancel?.();
      return setSelectedFilterTitle('');
    }
    return onClearAll();
  };

  /**
   * Executes the onOpen function passed as parameter, if any.
   * @example Fetch model filter count when selecting it.
   */
  const handleOnOpenChange = async (isOpening: boolean) => {
    if (isOpening) {
      await onOpen?.();
      setSelectedFilterTitle(title);
    } else {
      setSelectedFilterTitle('');
    }
  };

  // Partials
  const getFilterItem = () => {
    return (
      <FilterItem
        onClick={() => {
          onOpen?.();
          setSelectedFilterTitle(title);
        }}
        $isOpen={selectedFilterTitle === title}
        $inUse={inUse}
      >
        <FilterItemContainer>
          <FilterItemIcon $isOpen={selectedFilterTitle === title} $inUse={inUse}>
            {icon}
          </FilterItemIcon>
          <FilterItemInformation>
            <FilterItemTitle $isOpen={selectedFilterTitle === title} $inUse={inUse}>
              {title}
              {storedFilter && ':'}
            </FilterItemTitle>
            {storedFilter && (
              <FilterItemSelectedValue>
                {printedValues ? printedValues : storedFilter?.printValues()}
              </FilterItemSelectedValue>
            )}
          </FilterItemInformation>
        </FilterItemContainer>
      </FilterItem>
    );
  };

  /**
   * The order of apply and cancel button differs on Desktop and mobile
   * @param content
   * @returns
   */
  const getFilterContent = (content) => {
    return (
      <FilterContent>
        <FilterComponentContainer>{content}</FilterComponentContainer>
        <FilterActionButtonsContainer>
          {loading ? (
            <>
              <StyledShimmerTitle $flex="1" $height={'1.75rem'} $borderRadius={'2.125rem'} />
              <StyledShimmerTitle $flex="1" $height={'1.75rem'} $borderRadius={'2.125rem'} />
            </>
          ) : (
            <>
              <StyledSecondaryButton onPress={secondaryButtonOnClick}>
                {inMobile(windowWidth) ? t('filters.clearAll') : t('filters.cancel')}
              </StyledSecondaryButton>
              <StyledButton disabled={disableFilterButton} onPress={applyOnClick}>
                {t('filters.applyFilter')}
              </StyledButton>
            </>
          )}
        </FilterActionButtonsContainer>
      </FilterContent>
    );
  };

  return (
    <>
      {isMobile ? (
        <div>
          <div>{getFilterItem()}</div>
          {selectedFilterTitle === title ? (
            <FilterContentContainer>{getFilterContent(children)}</FilterContentContainer>
          ) : null}
        </div>
      ) : (
        <Popover
          isOpen={selectedFilterTitle === title}
          onOpenChange={(isOpening: boolean) => void handleOnOpenChange(isOpening)}
          placement="right-top"
          disableAnimation
        >
          <Popover.Trigger>{getFilterItem()}</Popover.Trigger>
          <FadeInAnimation duration={0.05}>
            <StyledPopoverContent>{getFilterContent(children)}</StyledPopoverContent>
          </FadeInAnimation>
        </Popover>
      )}
    </>
  );
};

export default BaseFilterComponent;
